// Update the handleSubmit function in useClassLogForm.js
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../../../store/slices/authSlice";
import {
	useCreateClassLogWithAbsencesMutation,
	useGetStudentsByDepartmentQuery,
} from "../../../../store/api/classLogApiSlice";
import { getWorkWeekDates, toMidnightUTC } from "../../../../utils/dateUtils";

export function useClassLogForm({
	departmentId,
	subjectId,
	disableDayAndPeriodSelection,
	externalDate,
	externalPeriod,
	subjects,
	onSuccess,
	onClose,
	substituteMode = false,
	substituteSubjectId = null,
}) {
	const user = useSelector(selectCurrentUser);
	const { assignedSubjects } = user || {};

	const [createClassLogWithAbsences, { isLoading: isCreating }] =
		useCreateClassLogWithAbsencesMutation();

	const {
		data: studentsData = [],
		isLoading: studentsLoading,
		error: studentsError,
	} = useGetStudentsByDepartmentQuery(departmentId, {
		skip: !departmentId,
	});

	const weekDays = getWorkWeekDates();
	const [selectedDay, setSelectedDay] = useState(
		weekDays.find((day) => day.value === new Date().toISOString().split("T")[0])
			?.value || ""
	);
	const [classHour, setClassHour] = useState("");
	const [lectureTitle, setLectureTitle] = useState("");
	const [classSequence, setClassSequence] = useState("");
	const [absentStudents, setAbsentStudents] = useState([]);
	const [studentOptions, setStudentOptions] = useState([]);
	const [teacherSubjects, setTeacherSubjects] = useState([]);
	const [selectedSubject, setSelectedSubject] = useState(subjectId || "");
	const [notifications, setNotifications] = useState([]);

	const isNewUiMode = disableDayAndPeriodSelection;
	const isLoading = isCreating || studentsLoading;

	const addNotification = (type, description) => {
		const id = Date.now();
		setNotifications((prev) => [...prev, { id, type, description }]);
	};

	const removeNotification = (id) => {
		setNotifications((prev) => prev.filter((n) => n.id !== id));
	};

	// Process students data from RTK Query
	useEffect(() => {
		if (studentsData && Array.isArray(studentsData)) {
			setStudentOptions(
				studentsData.map((s) => ({
					value: s.id,
					label: `${s.firstName} ${s.lastName}`,
				}))
			);
		}
	}, [studentsData]);

	// Handle students fetch error
	useEffect(() => {
		if (studentsError) {
			addNotification("error", "Failed to load students. Please try again.");
		}
	}, [studentsError]);

	// Setup teacher subjects for new UI mode (considering substitute mode)
	useEffect(() => {
		if (!isNewUiMode || !subjects || subjects.length === 0) {
			return;
		}

		let teacherSubs = [];

		if (substituteMode && substituteSubjectId) {
			// In substitute mode, only show the substitute subject
			const substituteSubject = subjects.find(
				(s) => s.id === substituteSubjectId
			);
			if (substituteSubject) {
				teacherSubs = [
					{
						value: substituteSubject.id,
						label: substituteSubject.name,
					},
				];
			}
		} else {
			// Normal mode - filter subjects by department
			const subsInDept =
				assignedSubjects?.filter((as) =>
					as.departmentId.includes(departmentId)
				) || [];

			teacherSubs = subsInDept.map((as) => {
				const foundSub = subjects.find((s) => s.id === as.subjectId);
				return {
					value: as.subjectId,
					label: foundSub ? foundSub.name : as.subjectId,
				};
			});
		}

		setTeacherSubjects(teacherSubs);

		if (!selectedSubject && teacherSubs.length > 0) {
			setSelectedSubject(teacherSubs[0].value);
		}
	}, [
		isNewUiMode,
		assignedSubjects,
		departmentId,
		subjects,
		selectedSubject,
		substituteMode,
		substituteSubjectId,
	]);

	const validateForm = () => {
		if (!lectureTitle.trim()) {
			addNotification("error", "Please fill in the lecture title.");
			return false;
		}

		if (!isNewUiMode) {
			if (!selectedDay) {
				addNotification("error", "Please select a day.");
				return false;
			}
			if (!classHour) {
				addNotification("error", "Please select a period.");
				return false;
			}
		}

		return true;
	};

	const handleSubmit = async () => {
		if (!validateForm()) return;

		let finalDate = selectedDay;
		let finalPeriod = classHour;
		let finalSubjectId = subjectId;

		if (isNewUiMode) {
			if (!externalDate || !externalPeriod) {
				addNotification(
					"error",
					"Error: Day and period selection is disabled but external values not provided."
				);
				return;
			}
			finalDate = externalDate;
			finalPeriod = externalPeriod;
			if (teacherSubjects.length > 0) {
				finalSubjectId = selectedSubject;
			}
		}

		const utcDate = toMidnightUTC(finalDate);
		const classLogData = {
			departmentId,
			subjectId: finalSubjectId,
			teacherId: user?.id,
			lectureTitle,
			lectureType: substituteMode ? "Substitute" : "Lecture",
			classDate: utcDate,
			period: finalPeriod,
			absentStudentIds: absentStudents.map((s) => s.value),
			sequence: classSequence ? parseInt(classSequence, 10) : undefined,
		};

		try {
			const newClassLog = await createClassLogWithAbsences(
				classLogData
			).unwrap();

			if (onSuccess) {
				onSuccess(newClassLog);
			}

			addNotification("success", "Class log created successfully!");
			setTimeout(() => {
				onClose();
			}, 1000);
		} catch (err) {
			if (err.status === 409) {
				addNotification(
					"error",
					"This day and period have already been logged. No duplicates allowed."
				);
			} else {
				addNotification(
					"error",
					`Error creating class log: ${err.message || "Please try again."}`
				);
			}
		}
	};

	return {
		isNewUiMode,
		weekDays,
		selectedDay,
		setSelectedDay,
		classHour,
		setClassHour,
		lectureTitle,
		setLectureTitle,
		classSequence,
		setClassSequence,
		absentStudents,
		setAbsentStudents,
		studentOptions,
		isLoading,
		teacherSubjects,
		selectedSubject,
		setSelectedSubject,
		notifications,
		removeNotification,
		handleSubmit,
	};
}
